import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useJobIdResolved, useReduxState } from 'hooks';
import api from 'api';
import { getMessageWithPlaceholders } from 'shared-locales';
import { useApiData } from 'lib/api';
import { getFailureMessageForCourseEvaluation } from 'lib/editor';
import { AIToolsStatus } from 'components/aiTools';
import {
  Alert,
  Button,
  FlexContainer,
  LoadingIndicator,
  ModalButton
} from 'components/common';
import { Icon } from 'components/common/icons';
import {
  Modal,
  ModalContent,
  ModalFooter,
  ModalPortal
} from 'components/common/modal';
import {
  EditorCourseEvaluationDisplay,
  EditorCourseEvaluationRunAnalysis
} from '.';

import type { GetCourseQualityEvaluationRes } from 'shared-types/responses';
import type { QuotaStatus } from 'components/aiTools/AIToolsStatus';
import type { ModalButtonRef } from 'components/common/ModalButton';

const EditorCourseEvaluation = () => {
  const { dispatch } = useDispatch();

  const {
    language,
    messages,
    pageState: { course },
    serverConfig: { aiExtraCourseEvaluation, defaultSystemLanguage },
    theme,
    user
  } = useReduxState();

  const modalButton = useRef<ModalButtonRef>(null);

  const [evaluationLanguage, setEvaluationLanguage] = useState(
    language || defaultSystemLanguage
  );
  const [evaluationRunning, setEvaluationRunning] = useState(false);
  const [jobId, setJobId] = useState<number | null>(null);
  const [isRerunningEvaluation, setIsRerunningEvaluation] = useState(false);
  const [quotaStatus, setQuotaStatus] = useState<QuotaStatus>('loading');

  useEffect(() => {
    if (user?.preferredLanguage) {
      setEvaluationLanguage(user.preferredLanguage);
    }
  }, [user?.preferredLanguage]);

  const { data, loading, updateData } =
    useApiData<GetCourseQualityEvaluationRes>({
      url: `/admin/courses/${course.id}/quality-evaluation`,
      onlyIf: aiExtraCourseEvaluation,
      onDataUpdated: (data) => {
        if (data.job?.id && data.job.state === 'inprogress') {
          dispatch.registerPendingJob({
            jobId: data.job.id,
            type: 'course-quality-evaluation'
          });
          setEvaluationRunning(true);
          setJobId(data.job.id);
          return;
        }

        setJobId(null);

        if (!data.job || data.job.state !== 'inprogress') {
          setEvaluationRunning(false);
        }
      }
    });

  const onJobResolved = useCallback(() => {
    updateData({ showLoading: false });
  }, [updateData]);

  useJobIdResolved({ callback: onJobResolved, jobId });

  const confirmReRunEvaluation = async (confirm: boolean) => {
    setIsRerunningEvaluation(false);
    if (confirm) await runEvaluation();
  };

  const getNumberOfPendingSuggestions = (
    data: GetCourseQualityEvaluationRes
  ) => {
    return (
      (data?.suggestions?.critical.filter(
        (suggestion) => suggestion.status === 'pending'
      ).length || 0) +
      (data?.suggestions?.recommended.filter(
        (suggestion) => suggestion.status === 'pending'
      ).length || 0) +
      (data?.suggestions?.optional.filter(
        (suggestion) => suggestion.status === 'pending'
      ).length || 0)
    );
  };

  const handleHideModal = () => {
    modalButton.current?.hideModal();
  };

  // update data when modal is opened in case someone else would have
  // started an evaluation after the editor was opened
  const handleShowModal = () => {
    updateData({ showLoading: false });
  };

  const runEvaluation = async () => {
    try {
      setEvaluationRunning(true);

      const response = await api.post(
        `/admin/courses/${course.id}/quality-evaluation`,
        { language: evaluationLanguage }
      );
      if (response.status === 202 && response.data.jobId) {
        dispatch.registerPendingJob({
          jobId: response.data.jobId,
          type: 'course-quality-evaluation'
        });
        setJobId(response.data.jobId);
        return; // evaluation is registered, avoid update data call
      }

      await updateData({ showLoading: true });
    } catch (error) {
      dispatch.handleErrorDispatch(error);
      setEvaluationRunning(false);
    }
  };

  const evaluation = data?.evaluation;
  const job = data?.job;

  const numberOfPendingSuggestions = useMemo(
    () => getNumberOfPendingSuggestions(data),
    [data?.suggestions]
  );

  // Wide modal only when a real evaluation is displayed
  // (not during in-progress or failure-without-evaluation states).
  const isShowingEvaluation =
    evaluation && !evaluationRunning && job?.state !== 'inprogress';

  const evaluationFailed = () => (
    <>
      <Alert
        style={{ marginBottom: 15 }}
        title={messages.courseEvaluationFailed}
        text={getFailureMessageForCourseEvaluation(job.error, messages)}
        type="error-light"
      />
      {evaluation && (
        <EditorCourseEvaluationDisplay
          evaluation={evaluation}
          suggestions={data?.suggestions}
          updateData={updateData}
        />
      )}
    </>
  );

  const evaluationInProgress = () => (
    <FlexContainer
      style={{
        flexDirection: 'column',
        justifyContent: 'center',
        padding: 20
      }}
    >
      <LoadingIndicator padding={10} />
      <div>{messages.courseEvaluationInProgress}</div>
      {jobId && (
        <div
          style={{
            fontSize: '1.4rem',
            fontWeight: 400,
            marginTop: 10,
            textAlign: 'center'
          }}
        >
          {messages.courseEvaluationInProgressBackground}
        </div>
      )}
    </FlexContainer>
  );

  const evaluationCompleted = () =>
    evaluation ? (
      <EditorCourseEvaluationDisplay
        evaluation={evaluation}
        suggestions={data?.suggestions}
        updateData={updateData}
      />
    ) : (
      <EditorCourseEvaluationRunAnalysis
        language={evaluationLanguage}
        onLanguageChange={setEvaluationLanguage}
      />
    );

  const renderModalBody = () => {
    if (loading) {
      return <LoadingIndicator />;
    }

    if (evaluationRunning) {
      return evaluationInProgress();
    }

    switch (job?.state) {
      case 'inprogress':
        // defensive fallback, but the evaluationRunning
        // check above should handle this as well
        return evaluationInProgress();
      case 'failed':
        return evaluationFailed();
      case 'completed':
      default:
        return evaluationCompleted();
    }
  };

  return (
    <div className="course-evaluation">
      <ModalButton
        category="lighter"
        getModalButton={(btn) => {
          modalButton.current = btn;
        }}
        modalProps={{
          title: messages.courseEvaluation,
          width: isShowingEvaluation
            ? 'wide'
            : evaluationRunning
            ? 'small'
            : 'medium'
        }}
        onModalShow={handleShowModal}
        size="header"
        style={{ height: 30, width: 30 }}
        text={
          <div className="button-icons">
            <div className="button-icons-main">
              <Icon icon="lightbulb" />
            </div>
            <div className="button-icons-side">
              <Icon icon="wand-magic-sparkles" />
            </div>
          </div>
        }
        tooltip={messages.courseEvaluation}
        {...(isShowingEvaluation
          ? { modalContentStyle: { backgroundColor: theme.lightGreyLighter } }
          : {})}
        {...(numberOfPendingSuggestions > 0
          ? {
              unreadItems: numberOfPendingSuggestions,
              unreadItemsTooltip: getMessageWithPlaceholders({
                message: messages.courseEvaluationPendingSuggestions,
                placeholders: { numberOfPendingSuggestions }
              })
            }
          : {})}
      >
        {quotaStatus === 'blocked' ? (
          <Alert
            style={{ marginBottom: 15 }}
            text={messages.aiToolsQuotaExceeded}
            type="warning-light"
          />
        ) : null}

        {renderModalBody()}

        <ModalButton.Footer>
          <div style={{ float: 'left' }}>
            <AIToolsStatus onReady={setQuotaStatus} style={{ padding: 0 }} />
          </div>

          <Button
            category="light"
            onClick={handleHideModal}
            text={evaluation ? messages.commonClose : messages.commonCancel}
          />

          <Button
            disabled={
              evaluationRunning ||
              job?.state === 'inprogress' ||
              quotaStatus !== 'allowed'
            }
            onClick={
              evaluation ? () => setIsRerunningEvaluation(true) : runEvaluation
            }
            text={
              evaluation
                ? messages.courseEvaluationReRun
                : messages.courseEvaluationRun
            }
          />
        </ModalButton.Footer>
      </ModalButton>

      {isRerunningEvaluation && (
        <ModalPortal>
          <Modal
            title={messages.courseEvaluationReRun}
            visible={isRerunningEvaluation}
            width="medium"
          >
            <ModalContent>
              <EditorCourseEvaluationRunAnalysis
                isRerun={true}
                language={evaluationLanguage}
                onLanguageChange={setEvaluationLanguage}
              />
            </ModalContent>

            <ModalFooter>
              <Button
                category="light"
                onClick={() => confirmReRunEvaluation(false)}
                text={messages.commonCancel}
              />
              <Button
                category="default"
                onClick={() => confirmReRunEvaluation(true)}
                text={messages.commonConfirm}
              />
            </ModalFooter>
          </Modal>
        </ModalPortal>
      )}

      <style jsx>{`
        .button-icons {
          align-items: center;
          display: flex;
          justify-content: center;
          position: relative;
        }
        .button-icons-main {
          font-size: 1.3rem;
          position: relative;
          left: -3px;
          top: -1px;
        }
        .button-icons-side {
          font-size: 0.8rem;
          padding: 2px;
          position: absolute;
          bottom: -5px;
          left: 4px;
        }
      `}</style>
    </div>
  );
};

export default EditorCourseEvaluation;
